// src/components/ContactSection.jsx
import { useState } from "react";
import toast from "react-hot-toast";

/**
 * ContactSection (Gantavyam – Final)
 * ----------------------------------------------------------
 * - Simple 4-field contact form (Name, Phone, Email, Message)
 * - Professional and minimal style (no marketing fluff)
 * - All editable data (texts + Google Form config) at the top
 * ----------------------------------------------------------
 */

// [Editable Google Form configuration]
const GOOGLE_FORM_URL =
    "https://docs.google.com/forms/d/e/1FAIpQLScRhe2-ktDIdHpN5DCT_EWpUefQgF9A7m-w-Ph26GCFb998hA/formResponse";

// [Replace these entry IDs with your own from Google Form]
const FORM_FIELDS = {
    name: "entry.2005620554",
    phone: "entry.1166974658",
    email: "entry.1045781291",
    message: "entry.839337160",
};

// [Editable content copy]
const content = {
    header: ["Let’s Start Reclaiming What’s Yours."],
    intro: [
        "Send me a message, and tell me more about your struggles in converting your physical shares. You can also book a free consultation to understand better.",
    ],
    submit: ["Get in Touch"],
    smallNote: ["Get instant response"],
};

export default function ContactSection() {
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        message: "",
    });

    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        let filteredValue = value;

        if (name === "name") filteredValue = value.replace(/[^a-zA-Z\s]/g, "");
        if (name === "phone") filteredValue = value.replace(/\D/g, ""); // only digits

        setForm((p) => ({ ...p, [name]: filteredValue }));
        setErrors((p) => ({ ...p, [name]: "" }));
    };

    const validate = () => {
        const newErrors = {};

        if (!form.name.trim() || form.name.trim().length < 2)
            newErrors.name = "Please enter your full name.";

        const phoneRegex = /^[1-9]\d{9}$/;
        if (!phoneRegex.test(form.phone))
            newErrors.phone = "Enter a valid 10-digit number.";

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(form.email))
            newErrors.email = "Enter a valid Email Id.";

        if (!form.message.trim() || form.message.trim().length < 10)
            newErrors.message =
                "Briefly describe your query (min 10 characters).";

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            toast.error("Please fill all the details.");
            return;
        }

        toast.loading("Submitting...", { id: "formSubmit" });

        try {
            const formData = new FormData();
            formData.append(FORM_FIELDS.name, form.name);
            formData.append(FORM_FIELDS.phone, form.phone);
            formData.append(FORM_FIELDS.email, form.email);
            formData.append(FORM_FIELDS.message, form.message);

            await fetch(GOOGLE_FORM_URL, { method: "POST", mode: "no-cors", body: formData });

            toast.success("Submitted. We will get back to you shortly.", { id: "formSubmit" });
            setForm({ name: "", phone: "", email: "", message: "" });
        } catch {
            toast.error("Failed to submit the form.", { id: "formSubmit" });
        }
    };

    return (
        <section id="contact" className="py-16 bg-[#CDE9DC] px-4 scroll-mt-10">
            <div className="max-w-2xl mx-auto text-center">
                {/* Top copy */}
                <h2
                    className="text-2xl sm:text-3xl font-semibold mb-3"
                    style={{ color: "var(--text-primary)" }}
                >
                    {content.header[0]}
                </h2>
                <p className="text-base mb-8" style={{ color: "var(--text-secondary)" }}>
                    {content.intro[0]}
                </p>

                {/* Contact Form */}
                <form
                    onSubmit={handleSubmit}
                    className="grid gap-4 text-left rounded-2xl p-6"
                    style={{
                        backgroundColor: "var(--bg-surface)",
                        color: "var(--text-primary)",
                        border: `1px solid var(--border)`,
                    }}
                    noValidate
                >
                    {/* Name */}
                    <div>
                        <input
                            name="name"
                            placeholder="Your Name"
                            value={form.name}
                            onChange={handleChange}
                            maxLength={40}
                            className="w-full px-4 py-3 rounded-md text-sm placeholder:text-[var(--text-secondary)] outline-none"
                            style={{
                                border: `1px solid var(--border)`,
                                backgroundColor: "transparent",
                                color: "var(--text-primary)",
                            }}
                        />
                        {errors.name && (
                            <p className="text-xs mt-1" style={{ color: "var(--color-error)" }}>
                                {errors.name}
                            </p>
                        )}
                    </div>

                    {/* Phone */}
                    <div>
                        <input
                            name="phone"
                            type="tel"
                            placeholder="Phone Number"
                            value={form.phone}
                            onChange={handleChange}
                            maxLength={10}
                            className="w-full px-4 py-3 rounded-md text-sm placeholder:text-[var(--text-secondary)] outline-none"
                            style={{
                                border: `1px solid var(--border)`,
                                backgroundColor: "transparent",
                                color: "var(--text-primary)",
                            }}
                        />
                        {errors.phone && (
                            <p className="text-xs mt-1" style={{ color: "var(--color-error)" }}>
                                {errors.phone}
                            </p>
                        )}
                    </div>

                    {/* Email */}
                    <div>
                        <input
                            name="email"
                            type="email"
                            placeholder="Email Id"
                            value={form.email}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-md text-sm placeholder:text-[var(--text-secondary)] outline-none"
                            style={{
                                border: `1px solid var(--border)`,
                                backgroundColor: "transparent",
                                color: "var(--text-primary)",
                            }}
                        />
                        {errors.email && (
                            <p className="text-xs mt-1" style={{ color: "var(--color-error)" }}>
                                {errors.email}
                            </p>
                        )}
                    </div>

                    {/* Message */}
                    <div>
                        <textarea
                            name="message"
                            placeholder="Your Message"
                            value={form.message}
                            onChange={handleChange}
                            rows={4}
                            maxLength={800}
                            className="w-full px-4 py-3 rounded-md text-sm placeholder:text-[var(--text-secondary)] outline-none resize-vertical"
                            style={{
                                border: `1px solid var(--border)`,
                                backgroundColor: "transparent",
                                color: "var(--text-primary)",
                            }}
                        />
                        {errors.message && (
                            <p className="text-xs mt-1" style={{ color: "var(--color-error)" }}>
                                {errors.message}
                            </p>
                        )}
                    </div>

                    {/* Submit */}
                    <div>
                        <button
                            type="submit"
                            className="w-full py-3 rounded-md font-semibold transition-transform hover:-translate-y-0.5"
                            style={{
                                backgroundColor: "var(--color-primary)",
                                color: "var(--bg-surface)",
                                border: `1px solid rgba(0,0,0,0.06)`,
                                boxShadow: "none", // professional, no glow
                            }}
                        >
                            {content.submit[0]}
                        </button>
                    </div>

                    {/* Note */}
                    <div className="mt-2 flex items-center justify-center gap-2 text-center">
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            className="flex-none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                            style={{ color: "var(--color-primary)" }}
                        >
                            <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" fill="currentColor" />
                        </svg>

                        <span className="text-sm italic" style={{ color: "var(--text-secondary)" }}>
                            {content.smallNote[0]}
                        </span>
                    </div>
                </form>
            </div>
        </section>
    );
}
